import { summerTimeChange, winterTimeChange } from './clockChange.js'
import { isCheckoutRoute } from './welcomeOffer.js'

const MINUTE = 60000

export const HALLOWEEN_IDLE_DELAY = 15000
export const HALLOWEEN_BATS_STORAGE_KEY = 'halloween_bats'

const HALLOWEEN_DAYS = [
  [9, 29],
  [9, 30],
  [9, 31],
  [10, 1],
  [10, 2],
  [10, 3],
]

export function parisOffsetMinutes(ms) {
  const year = new Date(ms).getUTCFullYear()
  return ms >= summerTimeChange(year) && ms < winterTimeChange(year) ? 120 : 60
}

export function isHalloweenPeriod(now) {
  if (!Number.isFinite(now)) return false
  const paris = new Date(now + parisOffsetMinutes(now) * MINUTE)
  const month = paris.getUTCMonth()
  const day = paris.getUTCDate()
  return HALLOWEEN_DAYS.some(([m, d]) => m === month && d === day)
}

export function showsHalloween({ now, enabled, routeName }) {
  return (
    Boolean(enabled) && isHalloweenPeriod(now) && !isCheckoutRoute(routeName)
  )
}

export function halloweenSeason(now) {
  return String(new Date(now).getUTCFullYear())
}

export function batsHaveFlown(stored, now) {
  return stored === halloweenSeason(now)
}

export function halloweenFor(state, routeName) {
  return showsHalloween({
    now: state.clock_now,
    enabled: state.halloween_enabled,
    routeName,
  })
}

const EASINGS = ['linear', 'ease-in', 'ease-out', 'ease-in-out']

const between = (random, min, max) => min + random() * (max - min)

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const toRadians = angle => (angle * Math.PI) / 180

const roundTenth = value => Math.round(value * 10) / 10

export function batScale(width) {
  if (width >= 1600) return 1.5
  if (width < 576) return 0.75
  return 1
}

export function batPath({ width, height, size, back, random }) {
  const steps = 7 + Math.floor(random() * 5)
  const margin = Math.max(size * 1.5, width * 0.08)
  const weights = Array.from({ length: steps }, () => between(random, 0.5, 1.5))
  const total = weights.reduce((sum, weight) => sum + weight, 0)
  let elapsed = 0
  let y = height * between(random, 0.08, 0.4)
  let depth = 1
  const points = Array.from({ length: steps + 1 }, (_, index) => {
    const edge = index === 0 || index === steps
    if (index > 0) {
      elapsed += weights[index - 1]
      y = clamp(
        y + (random() - 0.5) * height * 0.3,
        height * 0.04,
        height * 0.62
      )
      depth = clamp(depth + (random() - 0.5) * 0.24, 0.85, 1.15)
    }
    const swerve = edge ? 0 : (random() - 0.5) * width * 0.16
    const x = -margin + (index / steps) * (width + margin * 2) + swerve
    return {
      offset: index === steps ? 1 : elapsed / total,
      x: Math.round(back ? width - x - size : x),
      y: Math.round(y),
      scale: Math.round(depth * 100) / 100,
      easing: EASINGS[Math.floor(random() * EASINGS.length)],
    }
  })
  return points.map((point, index) => {
    const before = points[Math.max(0, index - 1)]
    const after = points[Math.min(points.length - 1, index + 1)]
    const dx = after.x - before.x
    const dy = after.y - before.y
    const climb = (Math.atan2(dy, Math.abs(dx) || 1) * 180) / Math.PI
    return {
      ...point,
      tilt: Math.round(clamp(climb * 0.6, -25, 25) * (dx < 0 ? -1 : 1)),
    }
  })
}

export function batFlights({ width, height, count = 6, random = Math.random }) {
  const scale = batScale(width)
  return Array.from({ length: count }, (_, index) => {
    const size = Math.round(between(random, 40, 72) * scale)
    return {
      size,
      delay: Math.round(index * 450 + between(random, 0, 400)),
      duration: Math.round(between(random, 5200, 8200)),
      flap: Math.round(between(random, 170, 260)),
      flutter: Math.round(between(random, 300, 560)),
      frames: batPath({ width, height, size, back: random() < 0.35, random }),
    }
  })
}

const BAT_WIDTH = 120
const BAT_SHOULDER = [54, 24]
const BAT_HIP = [55, 35]
const BAT_WRIST = [33, 15]
const BAT_THUMB = [30.5, 10.5]
const BAT_FINGERTIPS = [
  [3, 18],
  [11, 33],
  [26, 39],
  [42, 38],
]
const BAT_STROKES = [
  { angle: 42, reach: 0.74 },
  { angle: -30, reach: 1 },
]

const point = ([x, y]) => `${roundTenth(x)} ${roundTenth(y)}`

const swing = ([x, y], degrees, reach) => {
  const radians = toRadians(degrees)
  const dx = (x - BAT_SHOULDER[0]) * reach
  const dy = (y - BAT_SHOULDER[1]) * reach
  return [
    BAT_SHOULDER[0] + dx * Math.cos(radians) - dy * Math.sin(radians),
    BAT_SHOULDER[1] + dx * Math.sin(radians) + dy * Math.cos(radians),
  ]
}

const towards = (from, to, share) => [
  from[0] + (to[0] - from[0]) * share,
  from[1] + (to[1] - from[1]) * share,
]

const batWing = ({ angle, reach }, mirror) => {
  const flip = ([x, y]) => (mirror ? [BAT_WIDTH - x, y] : [x, y])
  const arm = 0.9 + 0.1 * reach
  const wrist = swing(BAT_WRIST, angle, arm)
  const thumb = swing(BAT_THUMB, angle, arm)
  const tips = BAT_FINGERTIPS.map(tip => swing(tip, angle, reach))
  const thumbBase = towards(wrist, tips[0], 0.07)
  const edge = [...tips, BAT_HIP]
  const scallops = edge.slice(1).map((end, index) => {
    const start = edge[index]
    const middle = towards(start, end, 0.5)
    return `Q${point(flip(towards(middle, wrist, 0.35)))} ${point(flip(end))}`
  })
  return {
    membrane: `M${[BAT_SHOULDER, wrist, thumb, thumbBase, tips[0]]
      .map(corner => point(flip(corner)))
      .join('L')}${scallops.join('')}Z`,
    fingers: `M${[BAT_SHOULDER, wrist, tips[0]]
      .map(joint => point(flip(joint)))
      .join('L')}${tips
      .slice(1)
      .map(tip => `M${point(flip(wrist))}L${point(flip(tip))}`)
      .join('')}`,
  }
}

export function batWings() {
  const strokes = [...BAT_STROKES, BAT_STROKES[0]]
  const side = mirror => {
    const poses = strokes.map(stroke => batWing(stroke, mirror))
    return {
      membrane: poses.map(pose => pose.membrane),
      fingers: poses.map(pose => pose.fingers),
    }
  }
  return { left: side(false), right: side(true) }
}

const WEB_SIZE = 200
const WEB_FRAME_GAP = 4
export const WEB_HUB = { x: 142, y: 58 }
const WEB_ANGLES = [
  -88, -62, -38, -14, 9, 33, 58, 84, 109, 133, 158, 184, 210, 236, 262,
]
const WEB_RINGS = [0.14, 0.24, 0.34, 0.45, 0.56, 0.67, 0.78, 0.88]
const WEB_SAG = 0.1

export function webReach(angle) {
  const dx = Math.cos(toRadians(angle))
  const dy = Math.sin(toRadians(angle))
  const hits = []
  if (dy < -0.001) hits.push(-WEB_HUB.y / dy)
  if (dx > 0.001) hits.push((WEB_SIZE - WEB_HUB.x) / dx)
  if (dy - dx > 0.001) {
    hits.push((WEB_HUB.x - WEB_HUB.y - WEB_FRAME_GAP) / (dy - dx))
  }
  return Math.min(...hits)
}

const webPoint = (angle, share) => {
  const length = webReach(angle) * share
  return [
    WEB_HUB.x + Math.cos(toRadians(angle)) * length,
    WEB_HUB.y + Math.sin(toRadians(angle)) * length,
  ]
}

const webRing = share =>
  WEB_ANGLES.map((angle, index) => {
    const next = WEB_ANGLES[(index + 1) % WEB_ANGLES.length]
    const [x1, y1] = webPoint(angle, share)
    const [x2, y2] = webPoint(next, share)
    const cx = (x1 + x2) / 2 + (WEB_HUB.x - (x1 + x2) / 2) * WEB_SAG
    const cy = (y1 + y2) / 2 + (WEB_HUB.y - (y1 + y2) / 2) * WEB_SAG
    const start = index === 0 ? `M${roundTenth(x1)} ${roundTenth(y1)}` : ''
    return `${start}Q${roundTenth(cx)} ${roundTenth(cy)} ${roundTenth(
      x2
    )} ${roundTenth(y2)}`
  }).join('')

export function spiderWeb() {
  const ends = WEB_ANGLES.map(angle => webPoint(angle, 1).map(roundTenth))
  return {
    ends,
    frame: `M${WEB_FRAME_GAP} 0L${WEB_SIZE} ${WEB_SIZE - WEB_FRAME_GAP}`,
    radials: ends
      .map(([x, y]) => `M${WEB_HUB.x} ${WEB_HUB.y}L${x} ${y}`)
      .join(''),
    rings: WEB_RINGS.map(webRing),
  }
}

const SPIDER_WIDTH = 60
const SPIDER_LEGS = [
  {
    joints: [
      [25, 42],
      [14, 45],
      [9, 57],
      [11, 67],
    ],
    twitch: 1.3,
  },
  {
    joints: [
      [24, 39],
      [11, 37],
      [4, 47],
      [1, 56],
    ],
    twitch: 1.7,
  },
  {
    joints: [
      [24, 35],
      [12, 28],
      [5, 31],
      [0, 38],
    ],
    twitch: 1.5,
  },
  {
    joints: [
      [25, 32],
      [16, 21],
      [12, 11],
      [14, 3],
    ],
    twitch: 1.9,
  },
]

export function spiderLegs() {
  const side = name =>
    SPIDER_LEGS.map(({ joints, twitch }, index) => {
      const [base, knee, ankle, tip] = joints.map(([x, y]) =>
        name === 'right' ? [SPIDER_WIDTH - x, y] : [x, y]
      )
      return {
        key: `${name}-${index}`,
        side: name,
        origin: `${base[0]}px ${base[1]}px`,
        twitch: name === 'right' ? Math.round(twitch * 113) / 100 : twitch,
        femur: `M${point(base)}L${point(knee)}`,
        tibia: `M${point(knee)}L${point(ankle)}`,
        tarsus: `M${point(ankle)}L${point(tip)}`,
        joints: [knee, ankle].map(([x, y]) => ({ x, y })),
      }
    })
  return [...side('left'), ...side('right')]
}
